import React, { useMemo, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  ErrorOutline as ErrorIcon,
  RemoveCircleOutline as SkipIcon,
  Star as DefaultIcon,
  WarningAmber as PartialIcon,
} from '@mui/icons-material';
import { MatrixCell, RunDetail } from '../../services/testsApi';
import { InputChips, expectedSummary, modelLabel } from './common';

type Status = 'pass' | 'partial' | 'fail' | 'error' | 'skipped' | 'none';
type Sort = 'hardest' | 'order';

export const cellStatus = (cell: MatrixCell | undefined): Status => {
  if (!cell) return 'none';
  if (cell.ok > 0) return cell.passed === cell.ok ? 'pass' : cell.passed > 0 ? 'partial' : 'fail';
  if (cell.errors) return 'error';
  if (cell.skipped) return 'skipped';
  return 'none';
};

const STATUS: Record<Status, { label: string; color: 'success' | 'warning' | 'error' | 'default'; icon: React.ReactElement | null; help: string }> = {
  pass: { label: 'Passed', color: 'success', icon: <PassIcon sx={{ fontSize: 15 }} />, help: 'Every run of the case passed' },
  partial: { label: 'Sometimes', color: 'warning', icon: <PartialIcon sx={{ fontSize: 15 }} />, help: 'Some runs passed, some failed' },
  fail: { label: 'Failed', color: 'error', icon: <FailIcon sx={{ fontSize: 15 }} />, help: 'No run passed' },
  error: { label: 'Error', color: 'error', icon: <ErrorIcon sx={{ fontSize: 15 }} />, help: 'The call failed' },
  skipped: { label: 'Skipped', color: 'default', icon: <SkipIcon sx={{ fontSize: 15 }} />, help: "The model can't take this input" },
  none: { label: '', color: 'default', icon: null, help: '' },
};

const cellText = (cell: MatrixCell) =>
  [
    `${cell.passed} of ${cell.ok} passed`,
    cell.errors ? `${cell.errors} error${cell.errors === 1 ? '' : 's'}` : '',
    cell.skipped ? `${cell.skipped} skipped` : '',
    cell.latency_ms !== null ? `${(cell.latency_ms / 1000).toFixed(1)}s avg` : '',
  ]
    .filter(Boolean)
    .join(' · ');

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

function useTone() {
  const theme = useTheme();
  return (s: Status) => {
    const c = STATUS[s].color;
    if (c === 'default') return { bg: theme.palette.action.hover, fg: theme.palette.text.secondary, border: theme.palette.divider };
    const main = theme.palette[c].main;
    return { bg: alpha(main, s === 'error' ? 0.06 : 0.14), fg: theme.palette[c].dark, border: alpha(main, 0.45) };
  };
}

/** One case on one model: score with a pass/fail colour and icon. */
export const ScoreTile: React.FC<{ cell: MatrixCell; title?: string; selected?: boolean; onClick?: () => void; width?: number }> = ({
  cell,
  title,
  selected,
  onClick,
  width = 84,
}) => {
  const theme = useTheme();
  const tone = useTone();
  const s = cellStatus(cell);
  const t = tone(s);
  return (
    <Tooltip title={title ?? cellText(cell)}>
      <Box
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={e => onClick && (e.key === 'Enter' || e.key === ' ') && onClick()}
        sx={{
          mx: 'auto',
          width,
          py: 0.5,
          borderRadius: 1.5,
          cursor: onClick ? 'pointer' : 'help',
          bgcolor: t.bg,
          color: t.fg,
          textAlign: 'center',
          border: `${selected ? 2 : 1}px solid ${selected ? theme.palette.primary.main : t.border}`,
          borderStyle: s === 'skipped' ? 'dashed' : 'solid',
          transition: 'transform 0.1s, box-shadow 0.1s',
          ...(onClick ? { '&:hover': { transform: 'translateY(-1px)', boxShadow: 2 } } : {}),
          '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}` },
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          {STATUS[s].icon}
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>
            {cell.score !== null ? cell.score.toFixed(0) : STATUS[s].label || '–'}
          </Typography>
        </Stack>
        {cell.ok > 1 && (
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, opacity: 0.85 }}>
            {cell.passed}/{cell.ok} runs
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

/** Case × model grid: each tile is the case's average score on that model, coloured by whether it passed. */
export const CaseMatrix: React.FC<{
  detail: RunDetail;
  models: string[];
  defaultModel: string | null | undefined;
  selected: { model: string; caseId: string };
  onSelect: (model: string, caseId: string) => void;
}> = ({ detail, models, defaultModel, selected, onSelect }) => {
  const theme = useTheme();
  const tone = useTone();
  const [sort, setSort] = useState<Sort>('hardest');
  const [failingOnly, setFailingOnly] = useState(false);

  const rows = useMemo(() => {
    const out = detail.cases.map((c, i) => {
      const cells = models.map(m => detail.matrix[c.id]?.[m]);
      const scores = cells.map(x => x?.score).filter((v): v is number => v !== null && v !== undefined);
      const graded = cells.filter(x => x && x.ok > 0);
      return {
        c,
        i,
        avg: mean(scores),
        passing: graded.filter(x => cellStatus(x) === 'pass').length,
        graded: graded.length,
        failing: cells.some(x => ['fail', 'partial', 'error'].includes(cellStatus(x))),
      };
    });
    const shown = failingOnly ? out.filter(r => r.failing) : out;
    return sort === 'hardest' ? [...shown].sort((a, b) => (a.avg ?? 101) - (b.avg ?? 101) || a.i - b.i) : shown;
  }, [detail, models, sort, failingOnly]);

  const columns = useMemo(
    () =>
      models.map(m => {
        const cells = detail.cases.map(c => detail.matrix[c.id]?.[m]).filter((x): x is MatrixCell => !!x);
        const graded = cells.filter(x => x.ok > 0);
        return {
          model: m,
          avg: mean(cells.map(x => x.score).filter((v): v is number => v !== null)),
          passing: graded.filter(x => cellStatus(x) === 'pass').length,
          graded: graded.length,
        };
      }),
    [detail, models],
  );

  const sticky = { position: 'sticky' as const, left: 0, zIndex: 2, bgcolor: 'background.paper' };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5, flex: 1 }}>
          {(['pass', 'partial', 'fail', 'error', 'skipped'] as Status[]).map(s => {
            const t = tone(s);
            return (
              <Tooltip key={s} title={STATUS[s].help}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25, borderRadius: 1, bgcolor: t.bg, color: t.fg, border: `1px solid ${t.border}` }}>
                  {STATUS[s].icon}
                  <Typography variant="caption">{STATUS[s].label}</Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
        <FormControlLabel
          control={<Switch size="small" checked={failingOnly} onChange={e => setFailingOnly(e.target.checked)} />}
          label={<Typography variant="body2">Only cases with failures</Typography>}
        />
        <ToggleButtonGroup size="small" exclusive value={sort} onChange={(_, v) => v && setSort(v)}>
          <ToggleButton value="hardest">Hardest first</ToggleButton>
          <ToggleButton value="order">Case order</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 3 }}>
        <Table size="small" sx={{ borderCollapse: 'separate' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...sticky, minWidth: 240, zIndex: 3 }}>Case</TableCell>
              {columns.map(col => (
                <TableCell key={col.model} align="center" sx={{ minWidth: 104, verticalAlign: 'bottom' }}>
                  <Stack alignItems="center" spacing={0.25}>
                    <Stack direction="row" alignItems="center" spacing={0.25}>
                      {col.model === defaultModel && (
                        <Tooltip title="Current default model">
                          <DefaultIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                      <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {modelLabel(col.model)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                      {col.graded ? `${col.passing}/${col.graded} pass` : '–'}
                    </Typography>
                  </Stack>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ minWidth: 110 }}>
                <Tooltip title="How many models passed this case">
                  <span>Models passing</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length && (
              <TableRow>
                <TableCell colSpan={models.length + 2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No failing cases. Nice.
                </TableCell>
              </TableRow>
            )}
            {rows.map(({ c, passing, graded }) => (
              <TableRow key={c.id} hover>
                <TableCell sx={sticky}>
                  <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{`Expected: ${expectedSummary(c.expected) || 'not set'}`}</span>}>
                    <Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {c.name}
                        </Typography>
                        {c.critical && <Chip size="small" color="warning" variant="outlined" label="critical" sx={{ height: 18 }} />}
                      </Stack>
                      <InputChips preview={c.preview} max={1} />
                    </Box>
                  </Tooltip>
                </TableCell>
                {models.map(m => {
                  const cell = detail.matrix[c.id]?.[m];
                  return (
                    <TableCell key={m} align="center" sx={{ p: 0.5 }}>
                      {cell ? (
                        <ScoreTile
                          cell={cell}
                          title={`${modelLabel(m)} · ${cellText(cell)}`}
                          selected={selected.model === m && selected.caseId === c.id}
                          onClick={() => onSelect(m, c.id)}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          –
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
                <TableCell align="right">
                  {graded ? (
                    <Stack alignItems="flex-end" spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {passing}/{graded}
                      </Typography>
                      <Box sx={{ width: 72, height: 5, borderRadius: 3, bgcolor: alpha(theme.palette.error.main, 0.25), overflow: 'hidden' }}>
                        <Box sx={{ width: `${(100 * passing) / graded}%`, height: '100%', bgcolor: 'success.main' }} />
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      –
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length > 1 && (
              <TableRow>
                <TableCell sx={{ ...sticky, fontWeight: 600 }}>Average score (all cases)</TableCell>
                {columns.map(col => (
                  <TableCell key={col.model} align="center">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {col.avg !== null ? col.avg.toFixed(0) : '–'}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
